import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'header-glass py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container-main flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <span className="font-heading text-2xl md:text-3xl font-semibold text-[#2D2D2D]">
            Decors <span className="text-[#C5A065]">Genius</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.name.toLowerCase()}`}
              className={`nav-link ${
                isActive(link.path) ? 'text-[#C5A065]' : 'text-[#4A4A4A]'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:+18888801455"
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#C5A065] transition-colors"
          >
            <Phone size={16} />
            <span className="font-medium">+1-888-880-1455</span>
          </a>
          <Link to="/contact">
            <Button
              data-testid="header-cta-button"
              className="btn-primary text-sm"
            >
              Book Free Consultation
            </Button>
          </Link>
        </div>

        {/* Mobile Menu */}
        <div className="lg:hidden flex items-center gap-3">
          <a
            href="tel:+18888801455"
            data-testid="mobile-phone-link"
            className="p-2 text-[#4A4A4A]"
          >
            <Phone size={20} />
          </a>
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-trigger"
                className="text-[#4A4A4A]"
              >
                <Menu size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full max-w-sm bg-white p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-6 border-b">
                  <span className="font-heading text-xl font-semibold">
                    Decors <span className="text-[#C5A065]">Genius</span>
                  </span>
                </div>
                <nav className="flex-1 py-6">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                      onClick={() => setIsOpen(false)}
                      className={`block px-6 py-4 text-lg font-medium transition-colors ${
                        isActive(link.path)
                          ? 'text-[#C5A065] bg-[#FAF9F6]'
                          : 'text-[#2D2D2D] hover:bg-[#FAF9F6]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </nav>
                <div className="p-6 border-t">
                  <Link to="/contact" onClick={() => setIsOpen(false)}>
                    <Button
                      data-testid="mobile-cta-button"
                      className="btn-primary w-full"
                    >
                      Book Free Consultation
                    </Button>
                  </Link>
                  <a
                    href="tel:+18888801455"
                    className="flex items-center justify-center gap-2 mt-4 text-[#4A4A4A]"
                  >
                    <Phone size={18} />
                    <span>+1-888-880-1455</span>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
